/**
 * ProfileModal - Full profile view/edit modal
 * Shows profile details and allows editing own profile
 */
import React, { useState, useEffect } from 'react';
import { Mail, Save, Loader2, UserPlus, Languages as LanguagesIcon, Video, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ProfileAvatar } from './ProfileAvatar';
import { formatRoleName } from '../../utils/displayFormat';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AGES_SERVED_LABELS = { adults: 'Adults', children: 'Children', both: 'Adults & Children' };
export const SERVICE_DELIVERY_LABELS = { telehealth: 'Telehealth', in_person: 'In-Person', both: 'Telehealth & In-Person' };

export const ProfileModal = ({ 
  isOpen, 
  onClose, 
  userId,
  currentUserId,
  canManageAccepting = false,
  canEditProfile = false,
  startInEdit = false,
  onProfileUpdated
}) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAccepting, setSavingAccepting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    bio: '',
    specialty: '',
    languages: '',
    ages_served: '',
    service_delivery: ''
  });

  const isOwnProfile = userId === currentUserId;
  const canEdit = isOwnProfile || canEditProfile;

  useEffect(() => {
    if (isOpen) setEditMode(!!startInEdit);
  }, [isOpen, userId, startInEdit]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !userId) return;
      
      setLoading(true);
      try {
        const token = sessionStorage.getItem('token');
        const endpoint = userId === currentUserId ? '/profiles/me' : `/profiles/${userId}`;
        const response = await fetch(`${API}${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setProfile(data);
          setFormData({
            full_name: data.full_name || '',
            bio: data.bio || '',
            specialty: data.specialty || '',
            languages: (data.languages || []).join(', '),
            ages_served: data.ages_served || '',
            service_delivery: data.service_delivery || ''
          });
        } else {
          console.error('Profile fetch failed:', response.status);
          toast.error('Failed to load profile');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isOpen, userId, currentUserId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const endpoint = userId === currentUserId ? '/profiles/me' : `/profiles/${userId}`;
      const response = await fetch(`${API}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({
          full_name: data.full_name || '',
          bio: data.bio || '',
          specialty: data.specialty || '',
          languages: (data.languages || []).join(', '),
          ages_served: data.ages_served || '',
          service_delivery: data.service_delivery || ''
        });
      } else {
        toast.error('Failed to load profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const endpoint = isOwnProfile ? '/profiles/me' : `/profiles/${userId}`;
      const response = await fetch(`${API}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          languages: formData.languages.split(',').map(s => s.trim()).filter(Boolean)
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditMode(false);
        toast.success('Profile updated successfully');
        if (onProfileUpdated) {
          onProfileUpdated(data);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = (newUrl) => {
    setProfile(prev => ({ ...prev, profile_pic_url: newUrl }));
    if (onProfileUpdated) {
      onProfileUpdated({ ...profile, profile_pic_url: newUrl });
    }
  };

  const handleToggleAccepting = async (next) => {
    setSavingAccepting(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API}/profiles/${userId}/accepting-new-clients`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accepting_new_clients: next })
      });
      if (response.ok) {
        setProfile(prev => ({ ...prev, accepting_new_clients: next }));
        toast.success(next ? 'Marked as accepting new clients' : 'Marked as not accepting new clients');
        onProfileUpdated?.({ ...profile, user_id: userId, accepting_new_clients: next });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch {
      toast.error('Failed to update status');
    } finally {
      setSavingAccepting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="profile-modal">
        <DialogHeader>
          <DialogTitle>
            {isOwnProfile ? 'My Profile' : 'User Profile'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : profile ? (
          <div className="space-y-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-start gap-4">
              <ProfileAvatar 
                user={profile} 
                size="xl" 
                editable={canEdit}
                uploadUserId={isOwnProfile ? 'me' : userId}
                onUploadSuccess={handleAvatarUpload}
              />
              <div className="flex-1 min-w-0">
                {editMode ? (
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                      placeholder="Enter your full name"
                      data-testid="profile-fullname-input"
                    />
                  </div>
                ) : (
                  <>
                    {/* Header mirrors the directory card layout:
                        "Name, Credentials" / accepting badge / "Role | Title" */}
                    <h2 className="text-xl font-semibold text-gray-900" data-testid="profile-header-name">
                      {profile.full_name || 'No name set'}
                      {profile.credentials ? `, ${profile.credentials}` : ''}
                    </h2>
                    {profile.is_provider && typeof profile.accepting_new_clients === 'boolean' && (
                      <span
                        className={`mt-1 inline-flex items-center gap-1 rounded-full border font-medium text-xs px-2 py-0.5 ${
                          profile.accepting_new_clients
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                            : 'bg-gray-100 text-gray-500 border-gray-300'
                        }`}
                        data-testid="profile-header-accepting-badge"
                      >
                        <UserPlus className="h-3 w-3" />
                        {profile.accepting_new_clients ? 'Accepting New Clients' : 'Not Accepting'}
                      </span>
                    )}
                    <div className="text-sm text-gray-600 mt-1" data-testid="profile-header-role-title">
                      {formatRoleName(profile.role)}
                      {profile.title && <span> | {profile.title}</span>}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Contact Info (read-only) */}
            <div className="space-y-2 border-t pt-4">
              <h3 className="text-sm font-medium text-gray-500">Contact Information</h3>
              {profile.email && (
                <div className="flex items-center text-sm text-gray-700">
                  <Mail className="h-4 w-4 mr-2 text-teal-600" />
                  <a href={`mailto:${profile.email}`} className="text-teal-600 hover:underline">
                    {profile.email}
                  </a>
                </div>
              )}
            </div>

            {/* Accepting New Clients — providers only; editable by the
                provider themselves or an admin */}
            {profile.is_provider && (
              <div className="space-y-2 border-t pt-4" data-testid="profile-accepting-section">
                <h3 className="text-sm font-medium text-gray-500">Availability</h3>
                <div className="flex items-center justify-between gap-3">
                  <span
                    className={`inline-flex items-center gap-1.5 text-sm font-medium ${
                      profile.accepting_new_clients ? 'text-emerald-700' : 'text-gray-600'
                    }`}
                    data-testid="profile-accepting-status"
                  >
                    <UserPlus className="h-4 w-4" />
                    {profile.accepting_new_clients === true
                      ? 'Accepting new clients'
                      : profile.accepting_new_clients === false
                        ? 'Not accepting new clients'
                        : 'Availability not set'}
                  </span>
                  {(isOwnProfile || canManageAccepting) && (
                    <Switch
                      checked={profile.accepting_new_clients === true}
                      onCheckedChange={handleToggleAccepting}
                      disabled={savingAccepting}
                      className="data-[state=checked]:bg-emerald-600"
                      data-testid="profile-accepting-toggle"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Specialty */}
            <div className="space-y-2 border-t pt-4">
              <h3 className="text-sm font-medium text-gray-500">Specialty</h3>
              {editMode ? (
                <Input
                  value={formData.specialty}
                  onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value }))}
                  placeholder="e.g., Family Therapy, CBT, EMDR"
                  data-testid="profile-specialty-input"
                />
              ) : (
                <p className="text-sm text-gray-700">
                  {profile.specialty || <span className="text-gray-400 italic">No specialty specified</span>}
                </p>
              )}
            </div>

            {/* Languages */}
            <div className="space-y-2 border-t pt-4" data-testid="profile-languages-section">
              <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                <LanguagesIcon className="h-3.5 w-3.5" /> Languages
              </h3>
              {editMode ? (
                <Input
                  value={formData.languages}
                  onChange={(e) => setFormData(prev => ({ ...prev, languages: e.target.value }))}
                  placeholder="e.g., English, Spanish, ASL (comma-separated)"
                  data-testid="profile-languages-input"
                />
              ) : profile.languages?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {profile.languages.map((lang, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">{lang}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No languages listed</p>
              )}
            </div>

            {/* Ages Served & Service Delivery */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-4">
              <div className="space-y-2" data-testid="profile-ages-section">
                <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" /> Ages Served
                </h3>
                {editMode ? (
                  <Select
                    value={formData.ages_served || 'unset'}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, ages_served: v === 'unset' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="profile-ages-select">
                      <SelectValue placeholder="Not specified" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">Not specified</SelectItem>
                      <SelectItem value="adults">Adults</SelectItem>
                      <SelectItem value="children">Children</SelectItem>
                      <SelectItem value="both">Adults & Children</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-gray-700" data-testid="profile-ages-value">
                    {AGES_SERVED_LABELS[profile.ages_served] || <span className="text-gray-400 italic">Not specified</span>}
                  </p>
                )}
              </div>
              <div className="space-y-2" data-testid="profile-delivery-section">
                <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                  <Video className="h-3.5 w-3.5" /> Service Mode
                </h3>
                {editMode ? (
                  <Select
                    value={formData.service_delivery || 'unset'}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, service_delivery: v === 'unset' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="profile-delivery-select">
                      <SelectValue placeholder="Not specified" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">Not specified</SelectItem>
                      <SelectItem value="telehealth">Telehealth</SelectItem>
                      <SelectItem value="in_person">In-Person</SelectItem>
                      <SelectItem value="both">Telehealth & In-Person</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-gray-700" data-testid="profile-delivery-value">
                    {SERVICE_DELIVERY_LABELS[profile.service_delivery] || <span className="text-gray-400 italic">Not specified</span>}
                  </p>
                )}
              </div>
            </div>

            {/* Bio */}
            <div className="space-y-2 border-t pt-4">
              <h3 className="text-sm font-medium text-gray-500">Bio</h3>
              {editMode ? (
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell us about yourself, your experience, and approach..."
                  rows={4}
                  data-testid="profile-bio-input"
                />
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {profile.bio || <span className="text-gray-400 italic">No bio added yet</span>}
                </p>
              )}
            </div>

            {/* Actions */}
            {canEdit && (
              <div className="flex justify-end gap-2 border-t pt-4">
                {editMode ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditMode(false);
                        setFormData({
                          full_name: profile.full_name || '',
                          bio: profile.bio || '',
                          specialty: profile.specialty || '',
                          languages: (profile.languages || []).join(', '),
                          ages_served: profile.ages_served || '',
                          service_delivery: profile.service_delivery || ''
                        });
                      }}
                      disabled={saving}
                      data-testid="profile-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="profile-save-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => setEditMode(true)}
                    variant="outline"
                    data-testid="profile-edit-btn"
                  >
                    Edit Profile
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Profile not found
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;
